import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { PERMISSIONS, canFeatureForRole, hasAnyPermissionForModule } from '../../utils/permissions';
import { useRolePermissionMatrix } from '../../hooks/useRolePermissionMatrix';
import { useReferenceData } from '../../contexts/ReferenceDataContext';
import '../../styles/Dashboard.css';

const MODULE_TILES_ADMIN = [
  { module: 'Teams', label: 'Teams', icon: '🎉', path: '/admin/teams-events' },
  { module: 'Matches', label: 'Matches', icon: '🎯', path: '/admin/matches' },
  { module: 'Assets', label: 'Assets', icon: '📦', path: '/admin/assets' },
  { module: 'People', label: 'People', icon: '👥', path: '/admin/people' },
  { module: 'Reports', label: 'Reports', icon: '📈', path: '/admin/reports' },
];

const AdminDashboardPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalAssets: 0,
    assignedAssets: 0,
    availableAssets: 0,
    maintenanceAssets: 0,
    totalUsers: 0,
    pendingAcknowledgements: 0,
    totalValue: 0,
  });
  const [recentHandovers, setRecentHandovers] = useState([]);
  const [statusDistribution, setStatusDistribution] = useState({
    available: 0,
    pending: 0,
    received: 0,
    issue_reported: 0,
  });

  const { rolePermissions, permissionsReady } = useRolePermissionMatrix();
  const { loading: referenceDataLoading } = useReferenceData();
  const normalizedUserRole = useMemo(() => {
    try {
      const u = JSON.parse(sessionStorage.getItem('user') || '{}');
      return String(u?.role || '').trim().toLowerCase();
    } catch (error) {
      return '';
    }
  }, []);

  const canViewAssets = useMemo(
    () =>
      canFeatureForRole(normalizedUserRole, rolePermissions, [
        PERMISSIONS.VIEW_ALL_ASSETS,
        PERMISSIONS.VIEW_ASSIGNED_ASSETS,
      ]),
    [normalizedUserRole, rolePermissions]
  );
  const canAddAssets = useMemo(
    () => canFeatureForRole(normalizedUserRole, rolePermissions, [PERMISSIONS.ADD_ASSETS]),
    [normalizedUserRole, rolePermissions]
  );
  const canCheckout = useMemo(
    () => canFeatureForRole(normalizedUserRole, rolePermissions, [PERMISSIONS.CHECKOUT]),
    [normalizedUserRole, rolePermissions]
  );
  const canCheckin = useMemo(
    () => canFeatureForRole(normalizedUserRole, rolePermissions, [PERMISSIONS.CHECKIN]),
    [normalizedUserRole, rolePermissions]
  );
  const canAddEmployee = useMemo(
    () => canFeatureForRole(normalizedUserRole, rolePermissions, [PERMISSIONS.ADD_EMPLOYEE]),
    [normalizedUserRole, rolePermissions]
  );

  const adminModuleTileVisible = useCallback(
    (moduleName) => {
      if (
        (moduleName === 'Teams' || moduleName === 'Matches') &&
        (!permissionsReady || referenceDataLoading)
      ) {
        return false;
      }
      return hasAnyPermissionForModule(normalizedUserRole, rolePermissions, moduleName);
    },
    [normalizedUserRole, rolePermissions, permissionsReady, referenceDataLoading]
  );

  useEffect(() => {
    if (!permissionsReady) return;

    let cancelled = false;

    const fetchDashboardData = async () => {
      try {
        setLoading(true);

        let assets = [];
        let totalAssets = 0;
        if (canViewAssets) {
          const assetsResponse = await api.getAssets({ limit: 10000 });
          assets = assetsResponse.data || [];
          totalAssets = assetsResponse.total || assets.length;
        }

        const usersResponse = await api.getUsers({ limit: 10000 });
        const users = usersResponse.data || [];
        const totalUsers = usersResponse.total || users.length;

        const handoversResponse = await api.getHandovers({ limit: 10000 });
        const handovers = handoversResponse.data || [];

        const pendingResponse = await api.getHandovers({
          acknowledgementStatus: 'pending',
          pendingFilter: 'pending_only',
          limit: 1,
        });

        const pendingAssets = assets.filter((a) => a.status === 'pending').length;
        const receivedAssets = assets.filter((a) => a.status === 'received').length;
        const issueReportedAssets = assets.filter((a) => a.status === 'issue_reported').length;
        const availableAssets = assets.filter((a) => a.status === 'available').length;
        const maintenanceAssets = assets.filter((a) => a.status === 'maintenance').length;
        const assignedAssets = pendingAssets + receivedAssets + issueReportedAssets;

        const totalValue = assets.reduce((sum, asset) => {
          return sum + (parseFloat(asset.cost) || 0);
        }, 0);

        const pendingHandovers = pendingResponse.total !== undefined ? pendingResponse.total : 0;

        const statusDist = {
          available: availableAssets,
          pending: pendingAssets,
          received: receivedAssets,
          issue_reported: issueReportedAssets,
        };

        if (cancelled) return;

        setStats({
          totalAssets,
          assignedAssets,
          availableAssets,
          maintenanceAssets,
          totalUsers,
          pendingAcknowledgements: pendingHandovers,
          totalValue,
        });

        setStatusDistribution(statusDist);
        setRecentHandovers(handovers.slice(0, 5));
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchDashboardData();
    return () => {
      cancelled = true;
    };
  }, [permissionsReady, canViewAssets]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: '#f59e0b',
      acknowledged: '#10b981',
      issue_reported: '#ef4444',
      available: '#10b981',
      assigned: '#3b82f6',
      maintenance: '#f59e0b',
      retired: '#6b7280'
    };
    return colors[status] || '#6b7280';
  };

  const calculatePercentage = (value, total) => {
    if (total === 0) return 0;
    return Math.round((value / total) * 100);
  };

  if (!permissionsReady || loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '400px' 
      }}>
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      {/* Actions */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'flex-end', 
        gap: '12px',
        marginBottom: '24px',
        flexWrap: 'wrap'
      }}>
        {canAddAssets && (
        <button 
          className="btn-primary"
          onClick={() => navigate('/admin/assets/add')}
        >
          + Add Asset
        </button>
        )}
        {canCheckout && (
        <button 
          className="btn-secondary"
          onClick={() => navigate('/admin/checkout')}
        >
          Check-Out
        </button>
        )}
        {canCheckin && (
        <button 
          className="btn-secondary"
          onClick={() => navigate('/admin/checkin')}
        >
          Check-In
        </button>
        )}
      </div>

      {/* Statistics Cards */}
      <div className="stats-grid">
        <div className="stat-card stat-primary">
          <div className="stat-icon-wrapper">
            <div className="stat-icon">📦</div>
          </div>
          <div className="stat-content">
            <h3 className="stat-value">{stats.totalAssets}</h3>
            <p className="stat-label">Total Assets</p>
            <div className="stat-trend">
              <span className="trend-up">All assets in system</span>
            </div>
          </div>
        </div>

        <div className="stat-card stat-success">
          <div className="stat-icon-wrapper">
            <div className="stat-icon">✅</div>
          </div>
          <div className="stat-content">
            <h3 className="stat-value">{stats.availableAssets}</h3>
            <p className="stat-label">Available Assets</p>
            <div className="stat-trend">
              <span className="trend-up">
                {calculatePercentage(stats.availableAssets, stats.totalAssets)}% of total
              </span>
            </div>
          </div>
        </div>

        <div className="stat-card stat-info">
          <div className="stat-icon-wrapper">
            <div className="stat-icon">👤</div>
          </div>
          <div className="stat-content">
            <h3 className="stat-value">{stats.assignedAssets}</h3>
            <p className="stat-label">Assigned Assets</p>
            <div className="stat-trend">
              <span className="trend-up">
                {calculatePercentage(stats.assignedAssets, stats.totalAssets)}% of total
              </span>
            </div>
          </div>
        </div>

        <div className="stat-card stat-warning">
          <div className="stat-icon-wrapper">
            <div className="stat-icon">⚠️</div>
          </div>
          <div className="stat-content">
            <h3 className="stat-value">{stats.pendingAcknowledgements}</h3>
            <p className="stat-label">Pending Acknowledgements</p>
            <div className="stat-trend">
              <span className="trend-warning">Requires attention</span>
            </div>
          </div>
        </div>

        <div className="stat-card stat-danger">
          <div className="stat-icon-wrapper">
            <div className="stat-icon">🔧</div>
          </div>
          <div className="stat-content">
            <h3 className="stat-value">{stats.maintenanceAssets}</h3>
            <p className="stat-label">Under Maintenance</p>
            <div className="stat-trend">
              <span className="trend-down">
                {calculatePercentage(stats.maintenanceAssets, stats.totalAssets)}% of total
              </span>
            </div>
          </div>
        </div>

        <div className="stat-card stat-purple">
          <div className="stat-icon-wrapper">
            <div className="stat-icon">💰</div>
          </div>
          <div className="stat-content">
            <h3 className="stat-value">{formatCurrency(stats.totalValue)}</h3>
            <p className="stat-label">Total Asset Value</p>
            <div className="stat-trend">
              <span className="trend-up">Combined worth</span>
            </div>
          </div>
        </div>

        <div className="stat-card stat-blue">
          <div className="stat-icon-wrapper">
            <div className="stat-icon">👥</div>
          </div>
          <div className="stat-content">
            <h3 className="stat-value">{stats.totalUsers}</h3>
            <p className="stat-label">Total Users</p>
            <div className="stat-trend">
              <span className="trend-up">Active employees</span>
            </div>
          </div>
        </div>
      </div>

      {/* Modules — visible when User Roles grants any permission in that module */}
      {/* {MODULE_TILES_ADMIN.some((t) => adminModuleTileVisible(t.module)) && (
        <div className="dashboard-card" style={{ marginBottom: '24px' }}>
          <div className="card-header">
            <h2 className="card-title">Modules</h2>
          </div>
          <div className="card-body">
            <div className="quick-actions-grid">
              {MODULE_TILES_ADMIN.filter((t) => adminModuleTileVisible(t.module)).map((t) => (
                <button
                  key={t.module}
                  type="button"
                  className="quick-action-btn"
                  onClick={() => navigate(t.path)}
                >
                  <div className="quick-action-icon">{t.icon}</div>
                  <div className="quick-action-label">{t.label}</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )} */}

      {/* Charts and Recent Activity */}
      <div className="dashboard-grid">
        {/* Asset Status Chart */}
        <div className="dashboard-card">
          <div className="card-header">
            <h2 className="card-title">Asset Status Distribution</h2>
          </div>
          <div className="card-body">
            <div className="status-chart">
              {Object.entries(statusDistribution).map(([status, count]) => {
                const percentage = calculatePercentage(count, stats.totalAssets);
                return (
                  <div key={status} className="status-item">
                    <div className="status-header">
                      <span className="status-name" style={{ textTransform: 'capitalize' }}>
                        {status}
                      </span>
                      <span className="status-count">{count}</span>
                    </div>
                    <div className="status-bar">
                      <div 
                        className="status-bar-fill"
                        style={{
                          width: `${percentage}%`,
                          backgroundColor: getStatusColor(status)
                        }}
                      ></div>
                    </div>
                    <div className="status-percentage">{percentage}%</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Recent Handovers */}
        <div className="dashboard-card">
          <div className="card-header">
            <h2 className="card-title">Recent Handovers</h2>
            <button 
              className="btn-link"
              onClick={() => navigate('/admin/acknowledgements')}
            >
              View All
            </button>
          </div>
          <div className="card-body">
            {recentHandovers.length > 0 ? (
              <div className="recent-list">
                {recentHandovers.map((handover) => (
                  <div key={handover._id || handover.id} className="recent-item">
                    <div className="recent-item-icon">
                      <div 
                        className="status-dot"
                        style={{ backgroundColor: getStatusColor(handover.acknowledgementStatus) }}
                      ></div>
                    </div>
                    <div className="recent-item-content">
                      <div className="recent-item-title">
                        {handover.asset?.assetTag || 'N/A'} - {handover.asset?.description || 'Asset'}
                      </div>
                      <div className="recent-item-meta">
                        Assigned to: {handover.assignedTo?.name || 'N/A'} • {formatDate(handover.assignedDate)}
                      </div>
                    </div>
                    <div className="recent-item-status">
                      <span 
                        className="status-badge"
                        style={{ backgroundColor: getStatusColor(handover.acknowledgementStatus) }}
                      >
                        {handover.acknowledgementStatus || 'pending'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-state">
                <p>No recent handovers</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="dashboard-card">
        <div className="card-header">
          <h2 className="card-title">Quick Actions</h2>
        </div>
        <div className="card-body">
          <div className="quick-actions-grid">
            {canAddAssets && (
            <button 
              className="quick-action-btn"
              onClick={() => navigate('/admin/assets/add')}
            >
              <div className="quick-action-icon">➕</div>
              <div className="quick-action-label">Add Asset</div>
            </button>
            )}
            {canCheckout && (
            <button 
              className="quick-action-btn"
              onClick={() => navigate('/admin/checkout')}
            >
              <div className="quick-action-icon">📤</div>
              <div className="quick-action-label">Check-Out Asset</div>
            </button>
            )}
            {canCheckin && (
            <button 
              className="quick-action-btn"
              onClick={() => navigate('/admin/checkin')}
            >
              <div className="quick-action-icon">📥</div>
              <div className="quick-action-label">Check-In Asset</div>
            </button>
            )}
            {canAddEmployee && (
            <button 
              className="quick-action-btn"
              onClick={() => navigate('/admin/people/add')}
            >
              <div className="quick-action-icon">👤</div>
              <div className="quick-action-label">Add Employee</div>
            </button>
            )}
            {canViewAssets && (
            <button 
              className="quick-action-btn"
              onClick={() => navigate('/admin/assets')}
            >
              <div className="quick-action-icon">📋</div>
              <div className="quick-action-label">View All Assets</div>
            </button>
            )}
            <button 
              className="quick-action-btn"
              onClick={() => navigate('/admin/acknowledgements')}
            >
              <div className="quick-action-icon">✅</div>
              <div className="quick-action-label">Acknowledgements</div>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboardPage;
